// Business time of the CMS: one IANA time zone with a fixed offset (no DST), chosen in the CMS
// settings (display.timeZone, default Korea time). The box and every database stay in UTC.
//
// Two uses, two rules:
//   display   every timestamp is shown in the zone in effect now (formatVnDateTime, toZoneInput)
//   days      a day key 'YYYY-MM-DD' (reports, rebate and quest periods, daily limits) is cut by the
//             zone that was in effect at that instant: the zone list carries every switch with the
//             instant it took effect, so a day already closed is never cut again. Keys never go
//             backwards: after a switch to a smaller offset the last day before it runs on until the
//             new zone's midnight. Every instant is in exactly one day and days follow each other.
// The names keep their old `vn` prefix (Vietnam time was the only zone before 2026-10-02).
export const DEFAULT_TIME_ZONE = 'Asia/Seoul';
const DAY_MS = 86_400_000;
const offsetCache = new Map();
function offsetOnce(tz, ms) {
    const f = new Intl.DateTimeFormat('en-US', {
        timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
    const p = Object.fromEntries(f.formatToParts(new Date(ms)).map((x) => [x.type, x.value]));
    const wall = Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), Number(p.hour), Number(p.minute), Number(p.second));
    return wall - Math.floor(ms / 1000) * 1000;
}
// The offset of `tz` in ms when it never changes (no DST, checked over two years), else null.
export function fixedOffsetMs(tz) {
    if (offsetCache.has(tz))
        return offsetCache.get(tz);
    let out = null;
    try {
        const y = new Date().getUTCFullYear();
        const offs = [y, y + 1].flatMap((yy) => [0, 3, 6, 9].map((m) => offsetOnce(tz, Date.UTC(yy, m, 15))));
        out = offs.every((o) => o === offs[0]) && Math.abs(offs[0]) <= 14 * 3_600_000 ? offs[0] : null;
    }
    catch {
        out = null;
    }
    offsetCache.set(tz, out);
    return out;
}
export const isFixedOffsetZone = (tz) => typeof tz === 'string' && /^[A-Za-z_]+(\/[A-Za-z0-9_+-]+){0,2}$/.test(tz) && fixedOffsetMs(tz) !== null;
const rawKey = (ms, off) => new Date(ms + off).toISOString().slice(0, 10);
let segs = [{ tz: DEFAULT_TIME_ZONE, from: -Infinity, off: fixedOffsetMs(DEFAULT_TIME_ZONE), floor: null }];
let spansKey = JSON.stringify([{ tz: DEFAULT_TIME_ZONE, from: null }]);
function segAt(ms) {
    let s = segs[0];
    for (const x of segs)
        if (x.from <= ms)
            s = x;
    return s;
}
function keyIn(list, ms) {
    let s = list[0];
    for (const x of list)
        if (x.from <= ms)
            s = x;
    const k = rawKey(ms, s.off);
    return s.floor && k < s.floor ? s.floor : k;
}
function buildSegs(spans) {
    if (!Array.isArray(spans) || !spans.length)
        throw new RangeError('time zone list is empty');
    const out = [];
    spans.forEach((s, i) => {
        const off = isFixedOffsetZone(s?.tz) ? fixedOffsetMs(s.tz) : null;
        if (off === null)
            throw new RangeError('time zone without a fixed offset: ' + String(s?.tz));
        const from = i === 0 ? -Infinity : Date.parse(String(s.from));
        if (i === 0 ? s.from != null : !Number.isFinite(from) || from <= out[i - 1].from)
            throw new RangeError('time zone list out of order');
        out.push({ tz: s.tz, from, off, floor: i === 0 ? null : keyIn(out, from - 1) });
    });
    return out;
}
// Throws when `spans` is not a valid zone list (sorted by `from`, the first one from null).
export function checkTimeZones(spans) {
    buildSegs(spans);
}
// Replaces the zone list. Throws on a bad list and keeps the old one.
export function setTimeZones(spans) {
    segs = buildSegs(spans);
    spansKey = JSON.stringify(spans.map((s, i) => ({ tz: s.tz, from: i === 0 ? null : new Date(s.from).toISOString() })));
}
export const setTimeZone = (tz) => setTimeZones([{ tz, from: null }]);
// What the day cut depends on: equal strings = the same days.
export const timeZonesKey = () => spansKey;
export const timeZoneSegments = () => segs;
// The zone in effect at `at` (now by default): the one every timestamp is shown in.
export const currentTimeZone = (at = new Date()) => segAt(at.getTime()).tz;
export const currentOffsetMs = (at = new Date()) => segAt(at.getTime()).off;
const ZONE_NAMES = { 'Asia/Seoul': 'Hàn Quốc', 'Asia/Ho_Chi_Minh': 'Việt Nam', 'Asia/Saigon': 'Việt Nam', UTC: 'UTC' };
export function utcOffsetText(off) {
    const m = Math.abs(off) / 60_000;
    return 'UTC' + (off < 0 ? '-' : '+') + Math.floor(m / 60) + (m % 60 ? ':' + String(m % 60).padStart(2, '0') : '');
}
// "giờ Hàn Quốc (UTC+9)": the zone now, for labels.
export function timeZoneLabel(at = new Date()) {
    const s = segAt(at.getTime());
    return `giờ ${ZONE_NAMES[s.tz] ?? s.tz} (${utcOffsetText(s.off)})`;
}
// The business day 'YYYY-MM-DD' holding instant `d`.
export function vnDayKey(d) {
    return keyIn(segs, d.getTime());
}
// Start of business day `YYYY-MM-DD` as a UTC instant: the first instant whose day is `day` or later.
// A day is 24 h long except the one a zone switch cuts; never add 24 h to it, take the next day's start.
export function vnDayStart(day) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(day))
        throw new RangeError('day must be YYYY-MM-DD');
    const mid = Date.parse(day + 'T00:00:00Z');
    if (Number.isNaN(mid))
        throw new RangeError('day must be YYYY-MM-DD');
    for (let i = 0; i < segs.length; i++) {
        const s = segs[i];
        const c = Math.max(s.from, mid - s.off);
        if (i === segs.length - 1 || c < segs[i + 1].from)
            return new Date(c);
    }
    throw new RangeError('unreachable');
}
// Calendar arithmetic on a day key.
export function addDayKey(day, n) {
    return new Date(Date.parse(day + 'T00:00:00Z') + n * DAY_MS).toISOString().slice(0, 10);
}
// [start of `from`, start of the day after `to`) as UTC instants.
export function dayRange(from, to) {
    return { start: vnDayStart(from), end: vnDayStart(addDayKey(to, 1)) };
}
// The offset of the zone in effect at instant `d` (the one its day is cut by).
export const offsetAtMs = (d) => segAt(d.getTime()).off;
// Hour of the day (0-23) of instant `d` in the zone in effect then.
export function zoneHour(d) {
    return new Date(d.getTime() + offsetAtMs(d)).getUTCHours();
}
// SQLite CURRENT_TIMESTAMP text ("YYYY-MM-DD HH:MM:SS", UTC) to a Date.
export function parseSqliteUtc(s) {
    const t = Date.parse(s.replace(' ', 'T') + (s.endsWith('Z') ? '' : 'Z'));
    if (Number.isNaN(t))
        throw new RangeError('bad sqlite timestamp: ' + s);
    return new Date(t);
}
// "dd/mm/yyyy HH:MM:SS" in the zone in effect now.
export function formatVnDateTime(d) {
    const x = d instanceof Date ? d : new Date(d);
    const v = new Date(x.getTime() + currentOffsetMs()).toISOString();
    return v.slice(8, 10) + '/' + v.slice(5, 7) + '/' + v.slice(0, 4) + ' ' + v.slice(11, 19);
}
// "YYYY-MM-DD HH:MM:SS" in the zone in effect now (CSV exports: sortable, read as a date by Excel).
export function formatZoneSortable(d) {
    return new Date(d.getTime() + currentOffsetMs()).toISOString().slice(0, 19).replace('T', ' ');
}
// <input type="datetime-local"> value 'YYYY-MM-DDTHH:MM' of an instant, in the zone in effect now ...
export function toZoneInput(d) {
    return new Date(d.getTime() + currentOffsetMs()).toISOString().slice(0, 16);
}
// ... and back: the instant such a value names in the zone in effect now (null when it is not one).
export function fromZoneInput(s) {
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(s))
        return null;
    const t = Date.parse(s + (s.length === 16 ? ':00' : '') + 'Z');
    return Number.isNaN(t) ? null : new Date(t - currentOffsetMs());
}
//# sourceMappingURL=vn-time.js.map